/* oxlint-disable @typescript-eslint/no-shadow */
import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { ChangeEventHandler, useCallback, useEffect, useRef } from 'react';

// ROLLBACK(last-price): trigger price type 셀렉터 재활성화 시 복원.
// import {
//   TriggerPriceTypeSelect,
//   defaultTriggerPriceType,
// } from '@flipster/features/src/shared/trigger-price-type-select';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import EditIcon from '@flipster/react/icon/generated/EditIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { FormItem, NumberInput } from '@flipster/ui';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import TpSlEstPnlTooltip from './TpSlEstPnl';
import { isLegActive, presetFromBasicTpSl, type AdvancedTpSlPreset } from './advanced-tpsl';
import { formBunja } from './bunja/form';
import AdvancedTpSlModal, { AdvancedTpSlDraftContext } from './modals/advanced-tpsl-modal';
import { formItem } from './styles';

// Advanced(Limit) TP/SL 진입점 + 적용 상태 요약 포함.
const TpSl = () => {
  const transTrade = useTranslations('Trade.r250401');
  const t = useTranslations();
  const overlay = useOverlay();
  const { symbol, symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { pricePrecision, quoteCurrency } = useAtomValue(symbolInfoWithDefaultAtom);
  const {
    tpSlAtom,
    updateTpSlAtom,
    priceTypeAtom,
    tpslAllowStatusAtom,
    isOrderPriceOrAmountEmptyAtom,
    isAdvancedTpSlConfiguredAtom,
    advancedTpSlPresetAtom,
    clearAdvancedTpSlAtom,
    isBboAtom,
    resetLimitPrice,
  } = useBunja(formBunja);
  const priceType = useAtomValue(priceTypeAtom);
  const { enabled: withTpSl, tp, sl, tpTriggerPriceType, slTriggerPriceType } = useAtomValue(tpSlAtom);
  const updateTpSl = useSetAtom(updateTpSlAtom);
  const tpslAllowStatus = useAtomValue(tpslAllowStatusAtom);
  const isOrderPriceOrAmountEmpty = useAtomValue(isOrderPriceOrAmountEmptyAtom);
  const isAdvancedTpSlConfigured = useAtomValue(isAdvancedTpSlConfiguredAtom);
  const advancedPreset = useAtomValue(advancedTpSlPresetAtom);
  const clearAdvancedTpSl = useSetAtom(clearAdvancedTpSlAtom);
  const [isBbo, setIsBbo] = useAtom(isBboAtom);

  useUncheckTpSlWhenNotAllowed(tpslAllowStatus, updateTpSl);

  const handleAdvancedClick = useCallback(() => {
    if (isOrderPriceOrAmountEmpty) {
      snackbar.error({ message: t('Trade.r260611.Please_enter_the_price_and_amount_before_using_advanced_tpsl') });
      return;
    }
    // 모달 진입 시 prefill: commit된 preset이 있으면 그대로(Edit), 없으면 직전 inline TP/SL 입력값으로 초안 구성.
    // side는 제출 전 미확정이므로 Long을 기본값으로 둔다(모달 탭에서 변경 가능).
    // 매 렌더 새 객체라 의존성에 넣으면 콜백이 재생성되므로, 모달을 여는 이 시점에만 계산한다.
    const initialDraft =
      advancedPreset ?? presetFromBasicTpSl('Long', { tp, sl, tpTriggerPriceType, slTriggerPriceType });
    overlay.open(({ isOpen, onClose, unmount }) => (
      <AdvancedTpSlDraftContext value={initialDraft}>
        <AdvancedTpSlModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
      </AdvancedTpSlDraftContext>
    ));
  }, [isOrderPriceOrAmountEmpty, overlay, advancedPreset, tp, sl, tpTriggerPriceType, slTriggerPriceType, t]);

  const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    updateTpSl({ type: 'enabled', value: Boolean(e.target.checked) });
    if (e.currentTarget.checked) {
      segment.track(TradeEvent.TpSlClick, { symbol, tab_type: priceType });
      if (isBbo) {
        setIsBbo(false);
        resetLimitPrice();
      }
    } else {
      // 체크 해제 시 설정해 둔 Advanced preset도 함께 초기화한다.
      clearAdvancedTpSl();
    }
  };

  if (tpslAllowStatus === 'not-supported') return <TpSlNotSupportedNotice />;

  return (
    <>
      <div className={hstack({ justifyContent: 'space-between', py: 12 })}>
        <div className={hstack({ gap: 8 })}>
          <div className={hstack({ gap: 4 })}>
            <Checkbox
              checked={withTpSl}
              onChange={onChange}
              css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
              disabled={tpslAllowStatus !== 'allow'}
            >
              {transTrade('Tp_sl_label')}
            </Checkbox>
            {/* preset 설정 시 적용 방향(Long/Short)을 체크박스 옆에 인라인 표기 (Figma 12116:34197) */}
            {isAdvancedTpSlConfigured && advancedPreset && (
              <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                {`(${advancedPreset.side})`}
              </span>
            )}
          </div>
          {tpslAllowStatus === 'disallow' && <TpSlLockedTooltip />}
        </div>
        {tpslAllowStatus === 'allow' && withTpSl && (
          <button
            type='button'
            onClick={handleAdvancedClick}
            className={hstack({
              gap: isAdvancedTpSlConfigured ? 4 : 2,
              textStyle: 'Text/M/Strong',
              color: 'content.default.level-3',
              cursor: 'pointer',
            })}
          >
            {isAdvancedTpSlConfigured ? (
              <>
                <EditIcon size={16} />
                {t('Autogen_common.r251126.s_Edit')}
              </>
            ) : (
              <>
                {t('Trade.r260611.Advanced')}
                <ChevronRightIcon size={16} />
              </>
            )}
          </button>
        )}
      </div>
      {withTpSl &&
        (isAdvancedTpSlConfigured && advancedPreset ? (
          <AdvancedTpSlSummary preset={advancedPreset} quoteCurrency={quoteCurrency} pricePrecision={pricePrecision} />
        ) : (
          <TpSlInlineFields />
        ))}
    </>
  );
};

export default TpSl;

// =============================================================================
// 하위 컴포넌트
// =============================================================================

/** TP/SL이 허용되지 않는(allow가 아닌) 상태로 바뀌면 체크박스를 강제 해제한다. */
const useUncheckTpSlWhenNotAllowed = (
  tpslAllowStatus: 'allow' | 'disallow' | 'not-supported',
  updateTpSl: (action: { type: 'enabled'; value: boolean }) => void,
) => {
  useEffect(() => {
    if (tpslAllowStatus !== 'allow') updateTpSl({ type: 'enabled', value: false });
  }, [tpslAllowStatus, updateTpSl]);
};

/** 인라인 간편 TP/SL 입력(Take Profit / Stop Loss). 오더북 자동 입력 시 커서 보정 포함. */
const TpSlInlineFields = () => {
  const transTrade = useTranslations('Trade.r250401');
  const { symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const { pricePrecision, quoteCurrency } = useAtomValue(symbolInfoWithDefaultAtom);
  const { tpSlAtom, updateTpSlAtom, focusedTpSlFieldAtom, tpSlAutofillSignalAtom } = useBunja(formBunja);
  // ROLLBACK(last-price): 셀렉터 재활성화 시 복원 — const { tp, sl, tpTriggerPriceType, slTriggerPriceType } = useAtomValue(tpSlAtom);
  const { tp, sl } = useAtomValue(tpSlAtom);
  const updateTpSl = useSetAtom(updateTpSlAtom);
  const setFocusedTpSlField = useSetAtom(focusedTpSlFieldAtom);
  const tpSlAutofillSignal = useAtomValue(tpSlAutofillSignalAtom);

  const tpInputRef = useRef<HTMLInputElement>(null);
  const slInputRef = useRef<HTMLInputElement>(null);

  const handleTpFocus = useCallback(() => setFocusedTpSlField('tp'), [setFocusedTpSlField]);
  const handleSlFocus = useCallback(() => setFocusedTpSlField('sl'), [setFocusedTpSlField]);
  const handleTpSlBlur = useCallback(() => setFocusedTpSlField(null), [setFocusedTpSlField]);

  // 입력 필드가 사라질 때(체크 해제/summary 전환) 포커스 상태를 정리한다.
  useEffect(() => () => setFocusedTpSlField(null), [setFocusedTpSlField]);

  // 오더북 등으로 TP/SL이 자동 채워지면 해당 입력의 커서를 끝으로 이동.
  // NumberInput 내부의 useEffect가 자식이라 먼저 실행되며 이전 cursor.current로 selection을
  // 옮기므로, 부모인 이 effect에서 마지막에 끝으로 다시 옮긴다.
  useEffect(() => {
    if (!tpSlAutofillSignal) return;
    const el = tpSlAutofillSignal.field === 'tp' ? tpInputRef.current : slInputRef.current;
    if (!el) return;
    const len = el.value.length;
    el.setSelectionRange(len, len);
  }, [tpSlAutofillSignal]);

  return (
    <div className={css({ pt: 8 })}>
      <FormItem
        label={
          <div className={hstack({ justifyContent: 'space-between', w: '100%', alignItems: 'center' })}>
            <p className={label}>{transTrade('Take_profit')}</p>
            {/* ROLLBACK(last-price): Last/Mark 셀렉터 — MM 준비 완료 시 재활성화.
            <TriggerPriceTypeSelect
              value={tpTriggerPriceType ?? defaultTriggerPriceType}
              onChange={(value) => updateTpSl({ type: 'tpTriggerPriceType', value })}
              placement='label-suffix'
            /> */}
          </div>
        }
        className={formItem}
      >
        <TpSlEstPnlTooltip field='tp'>
          <NumberInput
            ref={tpInputRef}
            precision={pricePrecision}
            placeholder={transTrade('Enter_price')}
            currency={quoteCurrency}
            value={tp}
            onChange={(tp) => updateTpSl({ type: 'tp', value: tp })}
            onFocus={handleTpFocus}
            onBlur={handleTpSlBlur}
          />
        </TpSlEstPnlTooltip>
      </FormItem>
      <FormItem
        label={
          <div className={hstack({ justifyContent: 'space-between', w: '100%', alignItems: 'center' })}>
            <p className={label}>{transTrade('Stop_loss')}</p>
            {/* ROLLBACK(last-price): Last/Mark 셀렉터 — MM 준비 완료 시 재활성화.
            <TriggerPriceTypeSelect
              value={slTriggerPriceType ?? defaultTriggerPriceType}
              onChange={(value) => updateTpSl({ type: 'slTriggerPriceType', value })}
              placement='label-suffix'
            /> */}
          </div>
        }
        className={formItem}
      >
        <TpSlEstPnlTooltip field='sl'>
          <NumberInput
            ref={slInputRef}
            precision={pricePrecision}
            placeholder={transTrade('Enter_price')}
            currency={quoteCurrency}
            value={sl}
            onChange={(sl) => updateTpSl({ type: 'sl', value: sl })}
            onFocus={handleSlFocus}
            onBlur={handleTpSlBlur}
          />
        </TpSlEstPnlTooltip>
      </FormItem>
    </div>
  );
};

const TpSlLockedTooltip = () => (
  <Tooltip placement='bottom-start' maxWidth={280} shape='gray'>
    <Tooltip.Trigger asChild>
      <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
    </Tooltip.Trigger>
    <Tooltip.Content>
      <h6 className={css({ textStyle: 'Text/M/Strong' })}>
        <FormattedMessage id='Trade.r260318.Tp_sl_disabled' />
      </h6>
      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
        <FormattedMessage id='Trade.r260318.Tp_sl_disabled_description' />
      </p>
    </Tooltip.Content>
  </Tooltip>
);

const TpSlNotSupportedNotice = () => {
  const t = useTranslations();
  const transTrade = useTranslations('Trade.r250401');
  const { priceTypeAtom, triggerTypeAtom } = useBunja(formBunja);
  const priceType = useAtomValue(priceTypeAtom);
  const triggerType = useAtomValue(triggerTypeAtom);
  const isTimeTriggerOrder = priceType === 'trigger' && triggerType === 'time';
  const message = isTimeTriggerOrder
    ? transTrade('Tp_sl_for_time_trigger_summary_description')
    : t('Trade.r260320.Tp_sl_trigger_order_description');

  return (
    <div className={css({ py: 16 })}>
      <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-2', mb: 8 })}>
        {transTrade('Tp_sl_label')}
      </p>
      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>{message}</p>
    </div>
  );
};

/**
 * Advanced TP/SL이 설정된 경우 인라인 입력 대신 per-leg 요약 박스를 보여준다. (Figma 12116:34197)
 * 적용 방향(Long/Short)은 헤더 체크박스 옆에 표기하고, 여기서는 leg 행만 렌더한다.
 * 예: "TP Limit  90,000.0 USDT" / "SL Market  64,000.0 USDT"
 * exec 라벨(Market/Limit)은 i18n 키로 연결됨. 'TP'/'SL' 약어는 공식 키 부재로 평문 유지(보류).
 */
const AdvancedTpSlSummary = ({
  preset,
  quoteCurrency,
  pricePrecision,
}: {
  preset: AdvancedTpSlPreset;
  quoteCurrency: string | undefined;
  pricePrecision: number;
}) => {
  const t = useTranslations();
  const legRow = (legType: 'tp' | 'sl') => {
    const leg = preset[legType];
    if (!isLegActive(leg)) return null;
    // 'TP'/'SL' 약어는 공식 키 부재로 평문 유지(보류).
    const name = legType === 'tp' ? 'TP' : 'SL';
    const exec = leg.executionType === 'limit' ? t('Trade.r251104.Limit') : t('Trade.r251008.Market');
    return (
      <div className={hstack({ justifyContent: 'space-between', w: '100%' })}>
        <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
          {`${name} ${exec}`}
        </span>
        <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-4' })}>
          <Formatted.Number value={leg.triggerPrice} precision={pricePrecision} toFixed /> {quoteCurrency}
        </span>
      </div>
    );
  };

  return (
    <div
      className={vstack({ gap: 8, alignItems: 'stretch', bg: 'background.default.level-1', rounded: 8, p: 12, mb: 16 })}
    >
      {legRow('tp')}
      {legRow('sl')}
    </div>
  );
};

const label = css({
  textStyle: 'Text/M/Strong',
  mb: 4,
  color: 'content.default.level-3',
});
